import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Save, Loader2, HelpCircle, Eye, EyeOff, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminFAQ, createFAQ, updateFAQ, deleteFAQ } from '@/lib/api';

const FAQRow = ({ faq, onEdit, onDelete, onToggleActive }) => {
  return (
    <div className={`p-4 bg-white rounded-lg border ${faq.is_active ? 'border-gray-200' : 'border-gray-200 bg-gray-50'} mb-3`}>
      <div className="flex items-start gap-3">
        <GripVertical className="w-4 h-4 text-gray-400 mt-1 cursor-move flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {!faq.is_active && (
              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Masquée</span>
            )}
            <span className="text-xs text-gray-400">#{faq.order}</span>
          </div>
          <h4 className={`font-medium mb-1 ${faq.is_active ? 'text-charcoal' : 'text-gray-400'}`}>
            {faq.question}
          </h4>
          <p className={`text-sm line-clamp-2 ${faq.is_active ? 'text-stone' : 'text-gray-400'}`}>
            {faq.answer}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleActive(faq)}
            className={faq.is_active ? 'text-green-600' : 'text-gray-400'}
            title={faq.is_active ? 'Masquer' : 'Afficher'}
          >
            {faq.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(faq)}
            className="text-blue-600"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(faq)}
            className="text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, faq: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, faq: null });
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    try {
      const response = await getAdminFAQ();
      setFaqs(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des FAQ');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      question: '',
      answer: '',
      order: faqs.length,
      is_active: true
    });
    setEditDialog({ open: true, faq: null });
  };

  const openEditDialog = (faq) => {
    setFormData({
      question: faq.question,
      answer: faq.answer,
      order: faq.order || 0,
      is_active: faq.is_active !== false
    });
    setEditDialog({ open: true, faq });
  };

  const handleSave = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Question et réponse sont obligatoires');
      return;
    }

    setSaving(true);
    try {
      if (editDialog.faq) {
        // Update existing
        await updateFAQ(editDialog.faq.id, formData);
        toast.success('FAQ mise à jour');
      } else {
        // Create new
        await createFAQ(formData);
        toast.success('FAQ créée');
      }
      setEditDialog({ open: false, faq: null });
      loadFAQs();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await updateFAQ(faq.id, {
        question: faq.question,
        answer: faq.answer,
        order: faq.order || 0,
        is_active: !faq.is_active
      });
      toast.success(faq.is_active ? 'FAQ masquée' : 'FAQ affichée');
      loadFAQs();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.faq) return;
    
    setSaving(true);
    try {
      await deleteFAQ(deleteDialog.faq.id);
      toast.success('FAQ supprimée');
      setDeleteDialog({ open: false, faq: null });
      loadFAQs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="FAQ">
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="FAQ">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-terracotta" />
            <span className="text-stone">{faqs.length} question(s)</span>
          </div>
          <Button
            data-testid="add-faq-btn"
            onClick={openCreateDialog}
            className="bg-terracotta hover:bg-terracotta/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une question
          </Button>
        </div>

        {/* FAQ List */}
        <Card>
          <CardHeader>
            <CardTitle>Questions fréquentes</CardTitle>
          </CardHeader>
          <CardContent>
            {faqs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune FAQ pour le moment</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={openCreateDialog}
                >
                  Créer la première question
                </Button>
              </div>
            ) : (
              faqs.map((faq) => (
                <FAQRow
                  key={faq.id}
                  faq={faq}
                  onEdit={openEditDialog}
                  onDelete={(f) => setDeleteDialog({ open: true, faq: f })}
                  onToggleActive={handleToggleActive}
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <div className="text-center text-sm text-gray-500">
          <p>Les questions marquées comme "masquées" ne seront pas visibles par les invités.</p>
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, faq: editDialog.faq })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-terracotta" />
              {editDialog.faq ? 'Modifier la question' : 'Nouvelle question'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-charcoal mb-1 block">Question</label>
              <Input
                data-testid="faq-question-input"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Ex: Comment confirmer ma présence ?"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-charcoal mb-1 block">Réponse</label>
              <Textarea
                data-testid="faq-answer-input"
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="Rédigez la réponse complète..."
                rows={5}
              />
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-charcoal mb-1 block">Ordre d'affichage</label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-24"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className="text-sm text-stone">Visible</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, faq: null })}>
              Annuler
            </Button>
            <Button
              data-testid="save-faq-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-terracotta hover:bg-terracotta/90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, faq: deleteDialog.faq })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer la question
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              Êtes-vous sûr de vouloir supprimer cette question ?
            </p>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-medium text-charcoal">{deleteDialog.faq?.question}</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, faq: null })}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
            >
              {saving ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminFAQ;
